import { toast } from '@bozic/ui/toast';
import axios, { type AxiosResponse } from 'axios';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { getType, type ActionType } from 'typesafe-actions';

import { getErrorMessage } from '@/utils/invariant';

import insuranceAPI from '../../services/insuranceService';
import { loadersActions } from '../loaders/actions';

import { accountPreferences } from './actions';

import type { AccountPreferencesDto } from '../../types/dto/AccountPreferencesDto';

export function* getAccountPreferences() {
  try {
    yield put(loadersActions.accountPreferencesLoader.activate());
    const accountPreferencesDtoAxiosResponse = (yield call(
      insuranceAPI.getAccountPreferences
    )) as AxiosResponse<AccountPreferencesDto>;
    yield put(accountPreferences.addAccountPreferences(accountPreferencesDtoAxiosResponse.data));
    yield put(loadersActions.accountPreferencesLoader.disable());
  } catch (e) {
    yield put(loadersActions.accountPreferencesLoader.disable());
    if (!axios.isAxiosError(e) || e.response?.status !== 404) {
      toast.error(getErrorMessage(e), {
        position: 'top-right'
      });
    }
    console.error(e);
  }
}

export function* updateAccountPreferences(
  action: ActionType<typeof accountPreferences.updateAccountPreferences>
) {
  try {
    yield put(loadersActions.accountPreferencesLoader.activate());
    yield call(insuranceAPI.updateAccountPreferences, action.payload);
    yield put(accountPreferences.saveUpdatedAccountPreferences(action.payload));
    yield put(loadersActions.accountPreferencesLoader.disable());
    yield toast.success('Changes Saved!', {
      position: 'top-right'
    });
  } catch (e) {
    yield put(loadersActions.accountPreferencesLoader.disable());
    toast.error(getErrorMessage(e), {
      position: 'top-right'
    });
    console.error(e);
  }
}

export function* updateAccountStarredPreferences(
  action: ActionType<typeof accountPreferences.setStarredOperations>
) {
  try {
    yield put(accountPreferences.saveUpdatedStarredOperations(action.payload));
    yield call(insuranceAPI.updateAccountStarredPreferences, action.payload);
  } catch (e) {
    toast.error(getErrorMessage(e), {
      position: 'top-right'
    });
    console.error(e);
  }
}

export function* updateAccountAndPassword(
  action: ActionType<typeof accountPreferences.updateAccountAndPassword>
) {
  try {
    yield put(loadersActions.accountPreferencesLoader.activate());
    const pref = {
      drpPriceReport: {
        frequency: action.payload.drpPriceReport.frequency,
        channel: action.payload.drpPriceReport.channel,
        operation: action.payload.drpPriceReport.operation
      },
      lrpPriceReport: {
        frequency: action.payload.lrpPriceReport.frequency,
        channel: action.payload.lrpPriceReport.channel,
        operation: action.payload.lrpPriceReport.operation
      },
      drpExpiredEndorsementsChannel: action.payload.drpExpiredEndorsementsChannel,
      lrpExpiredEndorsementsChannel: action.payload.lrpExpiredEndorsementsChannel,
      defaultOperation: action.payload.defaultOperation,
      timeZone: action.payload.timeZone,
      phoneNumber: action.payload.phoneNumber,
      alertChannel: action.payload.alertChannel,
      lrpAlertChannel: action.payload.lrpAlertChannel,
      smsOptedOut: action.payload.smsOptedOut
    };

    yield call(insuranceAPI.updateAccountPreferencesAndPassword, action.payload);
    yield put(accountPreferences.saveUpdatedAccountPreferences(pref));
    yield put(loadersActions.accountPreferencesLoader.disable());
    yield toast.success('Changes Saved!', {
      position: 'top-right'
    });
  } catch (e) {
    yield put(loadersActions.accountPreferencesLoader.disable());
    const message = getErrorMessage(e);
    if (message === 'Request failed with status code 400') {
      toast.error('The password you entered does not match the current password', {
        position: 'top-right'
      });
    } else {
      toast.error(message, {
        position: 'top-right'
      });
    }
  }
}

export function* sendMailNow(
  action: ActionType<typeof accountPreferences.sendDrpPriceReportEmail>
) {
  try {
    yield put(loadersActions.accountPreferencesLoader.activate());
    yield call(insuranceAPI.sendNewsletterToUser, action.payload);
    yield put(loadersActions.accountPreferencesLoader.disable());
    toast.success(
      `${action.payload.type === 'DrpPriceReport' ? 'DRP' : 'LRP'} Price Report is sent!`
    );
  } catch (error) {
    yield put(loadersActions.accountPreferencesLoader.disable());
    const responseData: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
    if (typeof responseData === 'string' && responseData) {
      toast.error(responseData);
    } else {
      toast.error(
        'Something went wrong and we were unable to send the report to your email address. We have informed the site administrators.'
      );
    }
  }
}

export function* accountPreferencesSagaWatcher() {
  yield all([
    takeLatest(getType(accountPreferences.getAccountPreferences), getAccountPreferences),
    takeLatest(getType(accountPreferences.updateAccountPreferences), updateAccountPreferences),
    takeLatest(getType(accountPreferences.updateAccountAndPassword), updateAccountAndPassword),
    takeLatest(getType(accountPreferences.sendDrpPriceReportEmail), sendMailNow),
    takeLatest(getType(accountPreferences.setStarredOperations), updateAccountStarredPreferences)
  ]);
}
